import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { InfoOutlined } from '@material-ui/icons/';
import moment from 'moment';
import { toast } from 'react-toastify';

import { MENU_WIDTH } from '../constants';
import { getPatient, editPatientData } from '../store/actions/patients';
import { getDevices, pauseDevices, stopPauseDevices, getDevicesStatus } from '../store/actions/devices';
import { DeviceInfo, EmptyDeviceInfo } from '../components/DeviceInfo';
import { getCurrentUser } from '../store/actions/auth';

import DeletePatientModal from '../components/DeletePatientModal.jsx';
import ExistingPatientModal from '../components/ExistingPatientModal.jsx';
import StopMonitoringButton from '../components/StopMonitoringButton.jsx';

const timeChoices = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hour' },
  { value: 240, label: '4 hour' },
  { value: 0, label: 'Until manually resumed' },
];

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    paddingLeft: MENU_WIDTH,
    paddingTop: '6rem',
  },
  top: {
    display: 'flex',
  },
  patient: {
    margin: '2rem 0 0 2rem',
    paddingRight: '1rem',
  },
  edit: {
    margin: '2rem 0 1rem 0',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  remove: {
    margin: '2rem 0 1rem 1rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  patientInfo: {
    margin: '0 0 0 2rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  column: {
    width: '48%',
  },
  row: {
    margin: '2rem 0 2rem 2rem',
    padding: '2rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
    border: '1px solid rgba(0, 0, 0, 0.12)',
  },
  icon: {
    color: '#FFCB75',
  },
  message: {
    margin: '0 2rem 0 1rem',
  },
  dropdown: {
    width: 200,
    margin: '0 2rem',
  },
  devicesRow: {
    margin: '2rem 0 2rem 2rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  card: {
    marginRight: '2rem',
  },
  pauseMenu: {
    display: 'flex',
  },
  button: {
    margin: '1rem 2rem',
  },
}));

const PatientDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const patient = useSelector((state) => state.patients.currentPatient);
  const devicesList = useSelector((state) => state.devices.devicesList);
  const patientDevices = useSelector((state) => state.patients.currentPatientDevices);
  const patientPauseState = useSelector((state) => state.patients.currentPatientPauseState);
  const [devices, setDevices] = useState([]);
  const classes = useStyles();
  const [editExistingPatientModalOpen, setEditExistingPatientModalOpen] = useState(false);
  const [deletePatientModalOpen, setRemovePatientModalOpen] = useState(false);
  const [timeChosen, setTimeChosen] = useState('');
  const [shouldShowPause, setShouldShowPause] = useState(true);
  const currentUser = useSelector((state) => state.admin.currentUserInfo);

  useEffect(() => {
    dispatch(getCurrentUser());
  }, []);

  useEffect(() => {
    dispatch(getPatient(id));
    dispatch(getDevices(true));
    const refreshTimer = setInterval(() => {
      dispatch(getPatient(id));
      dispatch(getDevices(true));
    }, 60000);
    return () => {
      clearInterval(refreshTimer);
    };
  }, [id, dispatch]);

  useEffect(() => {
    setDevices(patientDevices);
    dispatch(getDevicesStatus(patientDevices[0]?.id, patientDevices[1]?.id));
  }, [devicesList, patientDevices]);

  useEffect(() => {
    setShouldShowPause(patientPauseState === null);
  }, [patientPauseState]);

  const openLinkDevice = () => {
    history.push(`../../link?patient=${patient.id}`);
  };

  const handleTimeChosen = () => {
    if (timeChosen !== '') {
      dispatch(pauseDevices(devices[0]?.id, devices[1]?.id, timeChosen));
      setTimeChosen('');
    }
  };

  const handleSendTestAlert = () => {
    body = { send_test_alert:true };
    dispatch(editPatientData(body, id));
    toast.success('Scheduled test alert.', {
      position: 'bottom-left',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      theme: 'colored',
    });    
  };

  const handleResume = () => {
    dispatch(stopPauseDevices(devices[0]?.id, devices[1]?.id));
  };

  const handleNavigation = () => {
    sessionStorage.removeItem('ids');
    sessionStorage.removeItem('device');
    history.push(`../../history/${patient.id}`);
  };

  if (patient)
    return (
      <div className={classes.root}>
        <ExistingPatientModal open={editExistingPatientModalOpen} setOpen={setEditExistingPatientModalOpen} />
        <DeletePatientModal open={deletePatientModalOpen} setOpen={setRemovePatientModalOpen} />
        <div className={classes.top}>
          <h2 className={classes.patient}>
            {`Patient: ${patient.first_name} ${patient.middle_name} ${patient.last_name}`}
          </h2>
          <Button
            className={classes.edit}
            size="small"
            variant="contained"
            onClick={() => setEditExistingPatientModalOpen(true)}
          >
            Edit
          </Button>
          <Button
            className={classes.remove}
            size="small"
            variant="contained"
            onClick={() => setRemovePatientModalOpen(true)}
          >
            Remove
          </Button>
          <Button
            className={classes.remove}
            size="small"
            variant="contained"
            onClick={() => handleNavigation()}
          >
            Notification History
          </Button>
          <Button
            className={classes.remove}
            size="small"
            variant="contained"
            onClick={() => handleSendTestAlert()}
          >
            Send Test Notification
          </Button>

        </div>
        <div className={classes.patientInfo}>
          <div className={classes.column}>
            <p>
              <b>MRN: </b>
              {patient.mrn}
            </p>
            <p>
              <b>Unit: </b>
              {patient.unit}
            </p>
            <p>
              <b>Room Number: </b>
              {patient.room}
            </p>
          </div>
          <div className={classes.column}>
            {patient.notify_users && patient.notify_users.length > 0 && (
              <p>
                <b>Users to Notify: </b>
                {patient.notify_users.map(
                  (user, index) =>
                    `${user.first_name} ${user.last_name}${index < patient.notify_users.length - 1 ? ', ' : ''}`,
                )}
              </p>
            )}
            {patient.notify_groups && patient.notify_groups.length > 0 && (
              <p>
                <b>Groups to Notify: </b>
                {patient.notify_groups.map(
                  (group, index) => `${group.name}${index < patient.notify_groups.length - 1 ? ', ' : ''}`,
                )}
              </p>
            )}
          </div>
        </div>
        {(patient.device_a || patient.device_b) && (
          <div className={classes.pauseMenu}>
            {shouldShowPause && (
              <div className={classes.row}>
                <p>
                  <b>Pause Notifications: </b>
                </p>
                <FormControl variant="outlined" className={classes.dropdown} hiddenLabel size="small">
                  <Select value={timeChosen} onChange={(event) => setTimeChosen(event.target.value)}>
                    {timeChoices.map((choice) => (
                      <MenuItem value={choice.value} key={choice.value}>
                        {choice.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  className={classes.mainButton}
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={handleTimeChosen}
                >
                  Pause
                </Button>
              </div>
            )}
            {!shouldShowPause && (
              <div className={classes.row}>
                <InfoOutlined className={classes.icon} />
                <p className={classes.message}>
                  <b>
                    {`Notifications are paused until ${
                      patientPauseState === 0
                        ? 'they are manually resumed'
                        : moment(patientPauseState).format('M/DD/YY hh:mm a')
                    }`}
                  </b>
                </p>
                <Button
                  className={classes.mainButton}
                  color="primary"
                  size="small"
                  variant="contained"
                  onClick={handleResume}
                >
                  Resume Notifications
                </Button>
              </div>
            )}
            <div className={classes.row}>
              <span className={classes.stopMonitoring}>
                <StopMonitoringButton patient={patient} />
              </span>
            </div>
          </div>
        )}
        <div className={classes.devicesRow}>
          {devices.map((device, ix) =>
            device ? (
              <div className={classes.card} key={device.id}>
                <DeviceInfo device={device} actions />
              </div>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <div className={classes.card} key={`empty_device_${ix}`}>
                <EmptyDeviceInfo openLinkDevice={openLinkDevice} patient={patient} index={ix} />
              </div>
            ),
          )}
        </div>
        {currentUser.role && currentUser.role.name !== 'HCP' && currentUser.role.name !== 'Supervisor' && (
          <Button
            className={classes.button}
            color="primary"
            size="small"
            variant="contained"
            onClick={handleNavigation}
          >
            View Alert History
          </Button>
        )}
      </div>
    );
  return null;
};

export default PatientDetails;
